import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { useUser } from '../context/UserContext';
import ProfileBody, { type RecentSolve } from '../components/ProfileBody';
import { findProblemById } from '../data/problems';
import { useDocumentTitle } from '../lib/useDocumentTitle';

// Server-driven public profile. Field set varies by relationship — the body
// component renders only the sections present in `data`, so a stranger view
// gracefully collapses while a friend view is visually identical to /profile.

type Relationship = 'self' | 'friend' | 'stranger' | 'pending_out' | 'pending_in';

interface ServerSolve {
  problem_id: string;
  solved_at: string;
  xp_earned: number;
  topic: string;
}

interface ProfileResponse {
  user_id: string;
  handle: string;
  relationship: Relationship;
  level?: number;
  solve_streak_current?: number;
  total_solved?: number;
  solved_mid?: number;
  solved_cracked?: number;
  solved_goated?: number;
  solved_by_topic?: Record<string, number>;
  trophy_count?: number;
  trophies?: string[];
  recent_solves?: ServerSolve[];
  xp?: number;
  coins?: number;
  pending_in_count?: number;
}

const PublicProfilePage: React.FC = () => {
  const { handle: handleParam } = useParams<{ handle: string }>();
  useDocumentTitle(handleParam ? `@${handleParam}` : null);
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { refresh: refreshUser } = useUser();
  const idToken = authState.kind === 'authenticated' ? authState.idToken : undefined;

  const [data, setData] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Per-action transient message. Cleared on next action / page reload.
  const [actionMsg, setActionMsg] = useState('');

  const load = useCallback(async () => {
    if (!idToken || !handleParam) return;
    setLoading(true); setError('');
    try {
      const res = await api(`/api/users/@${handleParam}`, { token: idToken });
      if (res.status === 404) { setError('handle_not_found'); return; }
      if (!res.ok) { setError(`load failed (${res.status})`); return; }
      setData(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, [idToken, handleParam]);

  useEffect(() => { void load(); }, [load]);

  const act = async (kind: 'send' | 'accept' | 'remove') => {
    if (!idToken || !data) return;
    setBusy(true);
    setActionMsg('');
    try {
      let res: Response;
      if (kind === 'send') {
        res = await api('/api/friends/request', {
          method: 'POST', token: idToken, body: { target_handle: data.handle.replace(/^@/, '') },
        });
      } else if (kind === 'accept') {
        res = await api(`/api/friends/${data.user_id}/accept`, { method: 'POST', token: idToken });
      } else {
        res = await api(`/api/friends/${data.user_id}`, { method: 'DELETE', token: idToken });
      }
      if (!res.ok) {
        // Map the same status codes FriendsPage.sendRequest handles, plus
        // accept-specific 409 (no_pending_request) so the user sees why
        // the button quietly did nothing.
        const text =
          res.status === 404 ? (kind === 'send' ? 'no user with that handle' : 'user not found')
          : res.status === 409 ? (kind === 'send' ? 'already friends' : 'no pending request')
          : res.status === 400 ? "that handle isn't valid"
          : res.status === 429 ? 'slow down — too many requests'
          : `failed (${res.status})`;
        setActionMsg(text);
        return;
      }
      void refreshUser();
      void load();
    } catch (e) {
      setActionMsg(e instanceof Error ? e.message : 'failed');
    } finally {
      setBusy(false);
    }
  };

  // Translate server's snake_case + relationship into the props ProfileBody wants.
  const bodyProps = useMemo(() => {
    if (!data) return null;
    const recent: RecentSolve[] | undefined = data.recent_solves?.map(s => ({
      ts: Date.parse(s.solved_at),
      xp: s.xp_earned,
      problemId: s.problem_id,
      title: findProblemById(s.problem_id)?.title ?? s.problem_id,
    }));
    // Trophy detail is only sent when relationship is friend/self. For
    // stranger we get the count and render the gated placeholder.
    const trophyEarned = data.trophies
      ? Object.fromEntries(data.trophies.map(t => [t, true]))
      : undefined;
    return {
      username: data.handle.replace(/^@/, ''),
      level: data.level ?? 1,
      view: (data.relationship === 'self' ? 'self'
            : data.relationship === 'friend' ? 'friend'
            : data.relationship === 'pending_out' ? 'pending_out'
            : data.relationship === 'pending_in' ? 'pending_in'
            : 'stranger') as 'self' | 'friend' | 'stranger' | 'pending_in' | 'pending_out',
      streak: data.solve_streak_current ?? 0,
      solvedCount: data.total_solved ?? 0,
      solvedByTier: {
        mid: data.solved_mid ?? 0,
        cracked: data.solved_cracked ?? 0,
        goated: data.solved_goated ?? 0,
      },
      solvedByTopic: data.solved_by_topic ?? {},
      xp: data.xp,
      coins: data.coins,
      recentSolves: recent,
      trophyEarned,
      trophyCount: data.trophy_count ?? 0,
    };
  }, [data]);

  if (loading) {
    return <div style={{ padding: 32, fontSize: 12, color: 'var(--ink-faint)' }}>loading…</div>;
  }
  if (error === 'handle_not_found') {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-soft)' }}>
        <div style={{ fontSize: 17, fontWeight: 700 }}>no user @{handleParam}</div>
        <button className="sk-btn" style={{ marginTop: 12 }} onClick={() => navigate('/friends')}>back to friends</button>
      </div>
    );
  }
  if (error) {
    return <div style={{ padding: 32, fontSize: 12, color: 'var(--w-coral)' }}>{error}</div>;
  }
  if (!bodyProps || !data) return null;

  // Relationship-aware action chip beside the @handle.
  const actionSlot = (() => {
    if (data.relationship === 'self') return null;
    if (data.relationship === 'friend') {
      return <button className="sk-btn ghost" style={{ fontSize: 11 }} disabled={busy} onClick={() => act('remove')}>unfriend</button>;
    }
    if (data.relationship === 'pending_out') {
      return (
        <>
          <span className="sk-pill" style={{ fontSize: 10 }}>requested</span>
          <button className="sk-btn ghost" style={{ fontSize: 11, marginLeft: 6 }} disabled={busy} onClick={() => act('remove')}>cancel</button>
        </>
      );
    }
    if (data.relationship === 'pending_in') {
      return (
        <>
          <button className="sk-btn primary" style={{ fontSize: 11 }} disabled={busy} onClick={() => act('accept')}>accept</button>
          <button className="sk-btn ghost" style={{ fontSize: 11, marginLeft: 6 }} disabled={busy} onClick={() => act('remove')}>decline</button>
        </>
      );
    }
    return (
      <button className="sk-btn primary" style={{ fontSize: 11 }} disabled={busy} onClick={() => act('send')}>
        send request
      </button>
    );
  })();

  // Wrap the action buttons with a per-action message so 429 / 409 / 404
  // failures stop being silent — same UX as the find-tab in FriendsPage.
  const actionSlotWithMsg = actionSlot ? (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{actionSlot}</span>
      {actionMsg && (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--w-coral)' }}>
          {actionMsg}
        </span>
      )}
    </span>
  ) : null;

  return <ProfileBody {...bodyProps} actionSlot={actionSlotWithMsg} />;
};

export default PublicProfilePage;
